import React from 'react';
import type { SerializedEditorState } from 'lexical';
import { gql } from '@soundxyz/gql-string';
import type { PostModalSource } from '@soundxyz/utils/analytics';
import {
  CHANNELS_ERROR_ACTIONS,
  ERROR_TYPE,
  POSTING_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { useModal } from '@context/modal';
import {
  CreatePlaylistPostDocument,
  CreateReleasePostDocument,
  type FeedPostModalMainSoundFragment,
  useMutation,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useStableCallback } from '@hooks/useStableCallback';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER } from '@utils/constants';
import { AfterPlaylistPostModal } from './AfterFeedPost/AfterPlaylistPost';
import { AfterReleasePostModal } from './AfterFeedPost/AfterReleasePost';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation CreateReleasePost($input: CreateReleasePostInput!) {
    createReleasePost(input: $input) {
      __typename
      ... on MutationCreateReleasePostSuccess {
        data {
          __typename
          id
          webappUri

          ... on ReleasePost {
            ...AfterFeedPostReleaseInfo
          }
        }
      }

      ... on Error {
        message
      }
    }
  }

  mutation CreatePlaylistPost($input: CreatePlaylistPostInput!) {
    createPlaylistPost(input: $input) {
      __typename
      ... on MutationCreatePlaylistPostSuccess {
        data {
          __typename
          id
          webappUri

          ... on PlaylistPost {
            ...AfterFeedPostPlaylistInfo
          }
        }
      }

      ... on Error {
        message
      }
    }
  }
`);

export function useFeedPost() {
  const { openModal } = useModal();

  const logError = useLogError();

  const { isLoading: createReleaseIsLoading, mutateAsync: createReleasePostAsync } = useMutation(
    CreateReleasePostDocument,
    {},
  );

  const { isLoading: createPlaylistIsLoading, mutateAsync: createPlaylistPostAsync } = useMutation(
    CreatePlaylistPostDocument,
    {},
  );

  const createReleasePost = useStableCallback(
    async ({
      lexicalContent,
      release,
      sourceLocation,
      onPostCreated,
      channelId,
    }: {
      lexicalContent: SerializedEditorState | undefined;
      release: FeedPostModalMainSoundFragment;
      sourceLocation: PostModalSource;
      onPostCreated: () => void;
      channelId: string | null;
    }) => {
      try {
        const result = await createReleasePostAsync({
          input: {
            lexicalContent,
            releaseId: release.id,
            channelId,
          },
        });

        const resultData = result.data.createReleasePost;
        if (resultData.__typename === 'MutationCreateReleasePostSuccess') {
          if (resultData.data.__typename === 'ReleasePost') {
            openModal('AFTER_RELEASE_FEED_POST', {
              body: (
                <AfterReleasePostModal postFrag={resultData.data} sourceLocation={sourceLocation} />
              ),
            });
            onPostCreated();
          }
        } else {
          throw new Error('Failed to create release post', {
            cause: resultData,
          });
        }
      } catch (error) {
        logError({
          error,
          action: channelId
            ? CHANNELS_ERROR_ACTIONS.CREATE_POST_FAILED
            : POSTING_ERROR_ACTIONS.CREATE_POST_FAILED,
          errorType: ERROR_TYPE.POSTING,
          isArtistOnlyPage: false,
          level: 'error',
          message: "Couldn't create release post",
          toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
          pillar: channelId ? 'CHANNELS' : 'POSTING',
        });
      }
    },
  );

  const createPlaylistPost = useStableCallback(
    async ({
      playlistId,
      lexicalContent,
      sourceLocation,
      onPostCreated,
      channelId,
    }: {
      lexicalContent: SerializedEditorState | undefined;
      playlistId: string;
      sourceLocation: PostModalSource;
      onPostCreated: () => void;
      channelId: string | null;
    }) => {
      try {
        const result = await createPlaylistPostAsync({
          input: {
            lexicalContent,
            shelfId: playlistId,
            channelId,
          },
        });
        const resultData = result.data.createPlaylistPost;
        if (resultData.__typename === 'MutationCreatePlaylistPostSuccess') {
          if (resultData.data.__typename === 'PlaylistPost') {
            openModal('AFTER_PLAYLIST_FEED_POST', {
              body: (
                <AfterPlaylistPostModal
                  postFrag={resultData.data}
                  sourceLocation={sourceLocation}
                />
              ),
            });
            onPostCreated();
          }
        } else {
          throw new Error('Failed to create playlist post');
        }
      } catch (error) {
        logError({
          error,
          action: channelId
            ? CHANNELS_ERROR_ACTIONS.CREATE_POST_FAILED
            : POSTING_ERROR_ACTIONS.CREATE_POST_FAILED,
          errorType: ERROR_TYPE.POSTING,
          isArtistOnlyPage: false,
          level: 'error',
          message: "Couldn't create playlist post",
          toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
          pillar: channelId ? 'CHANNELS' : 'POSTING',
        });
      }
    },
  );

  return {
    createReleaseIsLoading,
    createReleasePostAsync,
    createPlaylistIsLoading,
    createPlaylistPostAsync,
    createReleasePost,
    createPlaylistPost,
  };
}
